import { DeviceType } from "../types/analytics";

/**
 * Detect device type based on user agent and screen dimensions
 */
export const detectDeviceType = (): DeviceType => {
	const userAgent = navigator.userAgent;
	const screenWidth = window.screen.width;

	// Mobile detection
	if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
		// Distinguish between mobile and tablet based on screen width
		if (screenWidth >= 768) {
			return DeviceType.TABLET;
		}
		return DeviceType.MOBILE;
	}

	// Tablet detection (additional cases)
	if (screenWidth >= 768 && screenWidth <= 1024) {
		return DeviceType.TABLET;
	}

	return DeviceType.DESKTOP;
};

/**
 * Get browser information
 */
export const getBrowserInfo = (): { name: string; version: string } => {
	const userAgent = navigator.userAgent;
	let browserName = "Unknown";
	let browserVersion = "Unknown";

	// Chrome
	if (userAgent.indexOf("Chrome") > -1) {
		browserName = "Chrome";
		const match = userAgent.match(/Chrome\/(\d+)/);
		if (match) browserVersion = match[1];
	}
	// Firefox
	else if (userAgent.indexOf("Firefox") > -1) {
		browserName = "Firefox";
		const match = userAgent.match(/Firefox\/(\d+)/);
		if (match) browserVersion = match[1];
	}
	// Safari
	else if (userAgent.indexOf("Safari") > -1) {
		browserName = "Safari";
		const match = userAgent.match(/Version\/(\d+)/);
		if (match) browserVersion = match[1];
	}
	// Edge
	else if (userAgent.indexOf("Edge") > -1) {
		browserName = "Edge";
		const match = userAgent.match(/Edge\/(\d+)/);
		if (match) browserVersion = match[1];
	}

	return { name: browserName, version: browserVersion };
};

/**
 * Get operating system information
 */
export const getOperatingSystem = (): string => {
	const userAgent = navigator.userAgent;

	if (userAgent.indexOf("Win") > -1) return "Windows";
	if (userAgent.indexOf("Mac") > -1) return "macOS";
	if (userAgent.indexOf("Linux") > -1) return "Linux";
	if (userAgent.indexOf("Android") > -1) return "Android";
	if (userAgent.indexOf("iPhone") > -1 || userAgent.indexOf("iPad") > -1) return "iOS";

	return "Unknown";
};

/**
 * Get screen resolution information
 */
export const getScreenInfo = () => {
	return {
		width: window.screen.width,
		height: window.screen.height,
		colorDepth: window.screen.colorDepth,
		pixelRatio: window.devicePixelRatio || 1,
	};
};

/**
 * Get timezone information
 */
export const getTimezone = (): string => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get language information
 */
export const getLanguage = (): string => {
	return navigator.language || "en-US";
};

/**
 * Check if user is online
 */
export const isOnline = (): boolean => {
	return navigator.onLine;
};

/**
 * Get comprehensive device metadata
 */
export const getDeviceMetadata = () => {
	const browser = getBrowserInfo();
	const screen = getScreenInfo();

	return {
		deviceType: detectDeviceType(),
		browser: browser.name,
		browserVersion: browser.version,
		operatingSystem: getOperatingSystem(),
		screen: {
			width: screen.width,
			height: screen.height,
			colorDepth: screen.colorDepth,
			pixelRatio: screen.pixelRatio,
		},
		timezone: getTimezone(),
		language: getLanguage(),
		isOnline: isOnline(),
		userAgent: navigator.userAgent,
	};
};
